import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  Tooltip,
  AreaChart as RechartsAreaChart,
  Area,
  YAxis
} from "recharts";
import { StageData } from "@/entities/reporting/types";
import { CustomTooltip } from "./Tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  avatarColorIndex,
  salesPipelineColors
} from "@/components/layouts/SalesPipeline/SalesColors";
import { getAcronyms } from "@/utilities";
import Text from "@/components/ui/text";
import { findIndex } from "lodash";

const MIN_AVATAR_GAP = 24;

interface Props {
  datasets: StageData[];
  maxYValue: number;
  height: number;
  index: number;
  isArr: boolean;
  isProbabilityMode: boolean;
  isAE: boolean;
}

type StageChartData = {
  stage: "current" | "next";
  [key: `data_${string}`]: number;
};

const AreaChartRecharts: React.FC<Props> = ({
  datasets,
  maxYValue,
  height,
  index,
  isArr,
  isProbabilityMode,
  isAE
}) => {
  const [activeUser, setActiveUser] = useState<string | null>(null);

  const currentStageValue = isProbabilityMode
    ? (datasets[index]?.totalWeightedValue ?? 0)
    : (datasets[index]?.totalValue ?? 0);

  const chartBarHeight =
    Math.max(0, (currentStageValue / maxYValue) * height) - 3;

  const sortedContributors = useMemo(() => {
    return Object.entries(datasets[0].contributors)
      .map(([userId, contrib]) => {
        let totalContribution = datasets.reduce((sum, dataset) => {
          const userContrib = dataset.contributors[userId];
          if (!userContrib) return sum;

          const value = isProbabilityMode
            ? (userContrib.weightedValue ?? 0)
            : (userContrib.value ?? 0);

          return sum + value;
        }, 0);

        return {
          userId,
          user: contrib.user,
          totalContribution
        };
      })
      .sort((a, b) => b.totalContribution - a.totalContribution);
  }, [datasets, isProbabilityMode]);

  const chartData = useMemo((): StageChartData[] => {
    const data: StageChartData[] = [{ stage: "current" }, { stage: "next" }];
    sortedContributors.forEach((contrib) => {
      const currentValue = isProbabilityMode
        ? (datasets[index].contributors[contrib.userId]?.weightedValue ?? 0)
        : (datasets[index].contributors[contrib.userId]?.value ?? 0);
      const nextValue =
        (isProbabilityMode
          ? datasets[index + 1]?.contributors[contrib.userId]?.weightedValue
          : datasets[index + 1]?.contributors[contrib.userId]?.value) ?? 0;
      data[0][`data_${contrib.userId}`] = currentValue;
      data[1][`data_${contrib.userId}`] = nextValue;
    });

    return data;
  }, [datasets, index, sortedContributors, isProbabilityMode]);

  // Track horizontal offsets for zero-value users
  const zeroValueOffsets = useMemo(() => {
    const zeroUsers = sortedContributors.filter(
      (contrib) => (chartData[0][`data_${contrib.userId}`] || 0) === 0
    );
    return Object.fromEntries(
      zeroUsers.map((user, idx) => [user.userId, (idx * MIN_AVATAR_GAP) / 2])
    );
  }, [sortedContributors, chartData]);

  const calculateHeight = (index: number) => {
    const currentUserId = sortedContributors[index].userId;
    const currentValue = chartData[0][`data_${currentUserId}`] || 0;

    if (currentValue === 0) {
      // All zero-value users at the same level (bottom)
      return "0px";
    }

    // For non-zero values, calculate position based on stack of non-zero values only
    // Only consider non-zero contributions before this user
    const nonZeroContributorsBefore = sortedContributors
      .slice(0, index)
      .filter((contrib) => (chartData[0][`data_${contrib.userId}`] || 0) > 0);

    const stackSum = nonZeroContributorsBefore.reduce(
      (sum, contrib) => sum + (chartData[0][`data_${contrib.userId}`] || 0),
      0
    );

    const currentHeight = Math.round((currentValue * height) / maxYValue);
    const centerPosition =
      Math.round((stackSum * height) / maxYValue) + currentHeight / 2;

    return `${centerPosition}px`;
  };

  // Calculate horizontal offset for zero-value users
  const calculateOffset = (userId: string): React.CSSProperties => {
    const currentValue = chartData[0][`data_${userId}`] || 0;

    if (currentValue === 0) {
      return {
        transform: `translateX(${zeroValueOffsets[userId]}px)`,
        bottom: "0px"
      };
    }

    return {
      bottom: calculateHeight(
        sortedContributors.findIndex((c) => c.userId === userId)
      )
    };
  };

  return (
    <div className="w-full relative -translate-x-1" style={{ height }}>
      {isAE && (
        <div
          className="absolute left-0 w-[6px] bg-charts-emerald-strong rounded z-10"
          style={{
            height: `${chartBarHeight}px`,
            bottom: 0
          }}
        />
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsAreaChart
          data={chartData}
          margin={{
            right: 4
          }}
        >
          <defs>
            {isAE ? (
              <>
                <linearGradient
                  id="backgroundGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="rgba(0, 245, 163, 0.18)" />
                  <stop offset="100%" stopColor="rgba(0, 245, 163, 0.09)" />
                </linearGradient>
                <clipPath id={`rounded-chart-area-${index}`}>
                  <rect width="100%" height="100%" rx="6" ry="6" />
                </clipPath>
                <pattern
                  id="single-pattern"
                  patternUnits="userSpaceOnUse"
                  width="100%"
                  height={height}
                >
                  <rect
                    x="0"
                    y="0"
                    width="100%"
                    height={height}
                    fill="url(#backgroundGradient)"
                  />
                </pattern>
              </>
            ) : (
              salesPipelineColors.map(({ fill, stroke }, idx) => (
                <pattern
                  id={`pattern-dataset${idx}`}
                  patternUnits="userSpaceOnUse"
                  width="100%"
                  height="100%"
                  key={`pattern-${idx}`}
                >
                  <rect width="10" height="100%" fill={stroke} />
                  <rect
                    x="10"
                    width="calc(100% - 10px)"
                    height="100%"
                    fill={fill}
                  />
                </pattern>
              ))
            )}
          </defs>
          <Tooltip
            content={
              <CustomTooltip
                index={index}
                datasets={datasets}
                userId={activeUser}
                userIndex={findIndex(
                  sortedContributors,
                  (contrib) => contrib.userId === activeUser
                )}
                isArr={isArr}
                isProbabilityMode={isProbabilityMode}
              />
            }
            cursor={false}
            wrapperStyle={{ zIndex: 50 }}
          />
          <YAxis
            type="number"
            domain={[0, maxYValue]}
            scale="linear"
            allowDataOverflow={true}
            hide
          />
          {sortedContributors.map((contrib, idx) => (
            <Area
              key={contrib.userId}
              dataKey={`data_${contrib.userId}`}
              stackId="1"
              fill={
                isAE
                  ? "url(#single-pattern)"
                  : `url(#pattern-dataset${avatarColorIndex(idx)}`
              }
              fillOpacity={1}
              activeDot={false}
              strokeWidth={0}
              onMouseEnter={() => setActiveUser(contrib.userId)}
              onMouseLeave={() => setActiveUser(null)}
              isAnimationActive={false}
              clipPath={isAE ? `url(#rounded-chart-area-${index})` : undefined}
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
      {!isAE &&
        index === 0 &&
        sortedContributors.map((contrib, idx) => {
          const currentValue = chartData[0][`data_${contrib.userId}`] || 0;
          const isZeroValue = currentValue === 0;

          return (
            <div
              key={contrib.userId}
              className="absolute -left-2 pointer-events-none flex gap-2 z-20"
              style={calculateOffset(contrib.userId)}
            >
              <Avatar className="!size-6 !rounded-full !text-sm">
                <AvatarFallback
                  className="border border-solid border-black text-h2 !rounded-full"
                  style={{
                    backgroundColor:
                      salesPipelineColors[avatarColorIndex(idx)].stroke
                  }}
                >
                  {getAcronyms(contrib.user?.displayName ?? "")}
                </AvatarFallback>
              </Avatar>
              {!isZeroValue && (
                <Text
                  as="div"
                  variant="body"
                  className="text-nowrap translate-y-0.5"
                >
                  {contrib.user?.displayName}
                </Text>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default AreaChartRecharts;
